<script setup>
  import { defineProps, defineEmits, computed } from 'vue'

  const props = defineProps({
    typeAlert: {
      type: String,
      default: 'success'
    },
    showAlert: {
      type: Boolean,
      default: true
    },
    showCloseButton: {
      type: Boolean,
      default: true
    },
    contentAlign: {
      type: String,
      default: 'start',
      validator: (value) => ['start', 'center', 'end'].includes(value)
    }
  })

  const isFeatureVariant = computed(() => props.typeAlert === 'feature')

  const contentAlignClass = computed(() => {
    return `justify-content-${props.contentAlign}`
  })

  const emit = defineEmits(['update:showAlert'])

  const alertClass = computed(() => {
    switch (props.typeAlert) {
      case 'success':
        return 'alert-notification-success'
      case 'success-soft':
          return 'alert-notification-success-soft'
      case 'error':
        return 'alert-notification-error'
      case 'warning':
        return 'alert-notification-warning'
      case 'warning-line':
        return 'alert-notification-warning-line'
      case 'info':
        return 'alert-notification-info'
      case 'info-soft':
        return 'alert-notification-info-soft'
      case 'announcement':
        return 'alert-notification-announcement'
      case 'feature':
        return 'alert-notification-feature'
      default:
        return 'alert-notification-success'
    }
  })

  const alertIcon = computed(() => {
    switch (props.typeAlert) {
      case 'success':
        return 'bi-check-circle-fill'
      case 'success-soft':
          return 'bi-check-circle-fill'
      case 'error':
        return 'bi-x-circle-fill'
      case 'warning':
        return 'bi-exclamation-triangle-fill'
      case 'warning-line':
        return 'bi-exclamation-triangle-fill'
      case 'info':
        return 'bi-info-circle-fill'
      case 'info-soft':
        return 'bi-info-circle-fill'
      case 'announcement':
        return 'bi-megaphone-fill'
      case 'feature':
        return ''
      default:
        return 'bi-check-circle-fill'
    }
  })

  const closeAlert = () => {
    emit('update:showAlert', false)
  }
</script>

<template>
  <Transition name="alert-slide">
    <div
      v-if="showAlert"
      :class="['alert-notification', 'text-14', alertClass, { 'alert-notification--feature': isFeatureVariant }]">
      <div v-if="props.showCloseButton" class="alert-notification__close" @click="closeAlert">
        <i class="bi bi-x-lg"></i>
      </div>
      <div :class="['alert-notification__message', 'd-flex', 'w-100', isFeatureVariant ? '' : contentAlignClass, { 'align-items-center': !isFeatureVariant }]">
        <i v-if="alertIcon" :class="['bi', alertIcon, 'align-self-center']"></i>
        <slot></slot>
      </div>
    </div>
  </Transition>
</template>
<style scoped lang="scss">
  // Slide-up dismiss transition
  .alert-slide-enter-active {
    transition: all 0.35s ease-out;
  }
  .alert-slide-leave-active {
    transition: all 0.35s ease-in;
  }
  .alert-slide-enter-from {
    opacity: 0;
    max-height: 0;
    margin-top: 0;
    margin-bottom: 0;
    padding-top: 0;
    padding-bottom: 0;
    overflow: hidden;
  }
  .alert-slide-leave-to {
    opacity: 0;
    max-height: 0;
    margin-top: 0;
    margin-bottom: 0;
    padding-top: 0;
    padding-bottom: 0;
    overflow: hidden;
  }
  .alert-slide-enter-to,
  .alert-slide-leave-from {
    max-height: 300px;
    overflow: hidden;
  }

  .alert-notification {
    box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.15);
    padding: 10px 12px;
    border-radius: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;

    &__message i {
      padding-right: 8px;
    }

    &__close {
      cursor: pointer;
      order: 1;
    }

    &-success, &-announcement {
      background-color: #ddece5;
      border: 1px solid var(--green-500);

      .alert-notification__message i,
      .alert-notification__message {
        color: var(--green-500);
      }
    }

    &-success-soft {
      background-color: var(--green-060);
      border: none;
      box-shadow: none;
      .alert-notification__message i,
      .alert-notification__message {
        color: var(--green-500);
      }
    }

    &-announcement {
      i{
        font-size: 32px;
        padding-right: 20px;
      }
      strong{
        font-size: 14px;
      }

      .alert-notification__close{
        display: flex;
        align-items: center;
        i{
          font-size: 18px;
          padding-right: 0;
        }
       
      }
    }

    &-error {
      background-color: #fae1e3;
      border: 1px solid #dc3545;
      .alert-notification__message i,
      .alert-notification__message {
        color: #dc3545;
      }
    }

    &-warning {
      background-color: #fff6da;
      border: 1px solid var(--bs-warning);
      .alert-notification__message i,
      .alert-notification__message {
        color: var(--bs-warning);
      }
    }

    &-warning-line {
      background-color: #fff6da;
      border: none;
      border-bottom: 1px solid var(--Components-Toasts-Header-Border, rgba(0, 0, 0, 0.05));
      box-shadow: none;
      .alert-notification__message i,
      .alert-notification__message {
        color: var(--bs-warning);
      }
    }

    &-info {
      background-color: #f1eff7;
      border: 1px solid #2e0c59;
      .alert-notification__message i,
      .alert-notification__message {
        color: #2e0c59;
      }
    }
    &-info-soft {
      background-color: #f7f1fd;
      border: none;
      box-shadow: none;
      .alert-notification__message i,
      .alert-notification__message {
        color: var(--bs-primary);
      }
    }

    // Feature announcement variant (white card with purple border)
    &--feature {
      background-color: #fff;
      border: 1px solid #C5CDD4;
      border-radius: 4px;
      padding: 7px 20px;
      margin-top: 30px;
      margin-bottom: 8px;
      box-shadow: 4px 4px 8px 0px rgba(0, 0, 0, 0.1);
      align-items: flex-start;

      .alert-notification__close {
        position: absolute;
        top: 16px;
        right: 16px;

        i {
          font-size: 20px;
          color: #6b7280;
          padding-right: 0;
        }

        &:hover i {
          color: #1a1a2e;
        }
      }

      .alert-notification__message {
        color: #1a1a2e;
      }
    }
  }
</style>
